import { useMemo, useState } from 'react';
import isEqual from 'lodash/isEqual';

import { isRelayEnabled } from './isRelayEnabled.ts';

function objToKey(obj: unknown): unknown | string {
	if (obj == null || typeof obj !== 'object') return obj;

	const sortedObj = Object.keys(obj)
		.sort() // we don't really care about sorting via localeCompare - the goal is to have this sorted the same way, irrespective of the actual comparator
		.reduce((result: Record<string, unknown>, key) => {
			result[key] = objToKey((obj as Record<string, unknown>)[key]);
			return result;
		}, {});
	return JSON.stringify(sortedObj);
}

/**
 * Utility hook to facilitate memoizing object parameters (e.g. `variables` or `context`) which mimics either Apollo or Relay behavior depending on the build variant
 */
export function useMemoObj<T extends Record<string, any>>(
	obj: T | undefined,
	callerPackage?: string,
): T {
	return isRelayEnabled(callerPackage)
		? // eslint-disable-next-line react-hooks/rules-of-hooks -- during runtime, there will be only one of those functions active
			useMemoObjRelay(obj ?? ({} as T))
		: // eslint-disable-next-line react-hooks/rules-of-hooks -- during runtime, there will be only one of those functions active
			useMemoObjApollo(obj ?? ({} as T));
}

function useMemoObjRelay<T>(obj: T): T {
	const [mirroredObj, setMirroredObj] = useState<T>(obj);
	if (isEqual(obj, mirroredObj)) {
		return mirroredObj;
	} else {
		// Generally, triggering side effects during the render phase is discouraged
		// This mimics Relay approach -- https://github.com/facebook/relay/blob/badd53/packages/react-relay/relay-hooks/useMemoVariables.js
		setMirroredObj(obj);
		return obj;
	}
}

function useMemoObjApollo<T extends Record<string, any>>(obj: T): T {
	return useMemo(
		() => obj,
		// eslint-disable-next-line react-hooks/exhaustive-deps -- this is mimicking Apollo's memoization approach; see https://github.com/trojanowski/react-apollo-hooks/blob/682be73/src/useQuery.ts#L119
		[obj && objToKey(obj)],
	);
}
